import React, { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useMutation, useQuery } from 'hooks/apollo';
import { gql } from '@apollo/client';

import { Button, Tag } from 'antd';
import { CopyFilled, DeleteFilled, EditOutlined, PlusOutlined } from '@ant-design/icons';
import Page from 'components/Page';
import Table from 'components/Table';

import globalMessages from 'translations/messages';
import StatusTag from 'components/StatusTag';
import { useSafeDelete } from 'hooks/useUserConfirmation';
import EditModuleLicenseDrawer from './EditModuleLicenseDrawer';

const QUERY = gql`
  query GetModuleLicenses {
    moduleLicenses {
      id
      title
      uniqueTitle
      status
      level
      module
      activeDataSources
    }
  }
`;

const DUPLICATE_MUTATION = gql`
  mutation DuplicateModuleLicense($id: ID!) {
    duplicateModuleLicense(id: $id) {
      id
    }
  }
`;

const DELETE_MUTATION = gql`
  mutation DeleteModule($id: ID!) {
    deleteModuleLicense(id: $id) {
      id
    }
  }
`;

const columns = [
  {
    dataIndex: 'module',
    title: 'Type',
    render: (module) => <Tag><FormattedMessage {...globalMessages[module]} /></Tag>,
    align: 'center',
    defaultSortOrder: 'ascend',
  },
  {
    dataIndex: 'uniqueTitle',
    title: 'Internal title',
  },
  {
    dataIndex: 'title',
    title: 'Display title',
  },
  {
    dataIndex: 'level',
    title: 'Level',
    type: 'number',
  },
  {
    dataIndex: 'status',
    title: 'Status',
    render: (status) => <StatusTag status={status} />,
    align: 'center',
  },
  {
    dataIndex: 'activeDataSources',
    title: <FormattedMessage {...globalMessages.dataSources} />,
    type: 'number',
  },
];

const ModulesLicenses = () => {
  const { data, loading, refetch } = useQuery(QUERY);
  const [editLicense, setEditLicense] = useState(false);
  const [duplicateLicense] = useMutation(DUPLICATE_MUTATION, { onCompleted: refetch });
  const [deleteLicense] = useMutation(DELETE_MUTATION, { onCompleted: refetch });
  const safeDelete = useSafeDelete();

  const dataSource = useMemo(() => data?.moduleLicenses.map((license) => ({
    ...license,
    uniqueTitle: license.uniqueTitle || license.title,
  })), [data]);

  const actions = (record) => (
    <Button type="primary" icon={<EditOutlined />} onClick={() => setEditLicense(record)}>
      Edit
    </Button>
  );

  const actionsMenuItems = (record) => [
    {
      key: 'duplicate',
      icon: <CopyFilled />,
      label: 'Duplicate',
      onClick: () => duplicateLicense({ variables: { id: record.id } }),
    },
    {
      key: 'delete',
      icon: <DeleteFilled />,
      label: 'Delete',
      danger: true,
      onClick: () => safeDelete(() => deleteLicense({ variables: { id: record.id } })),
      visible: record.activeDataSources === 0,
    },
  ];

  return (
    <Page>
      <Page.Header
        title={<FormattedMessage {...globalMessages.modules} />}
      />
      <Page.Content>
        <Table
          loading={loading}
          columns={columns}
          dataSource={dataSource}
          actions={actions}
          actionsMenuItems={actionsMenuItems}
          footerActions={(
            <Button type="primary" onClick={() => setEditLicense({})} icon={<PlusOutlined />}>
              <FormattedMessage {...globalMessages.create} />
            </Button>
          )}
        />
        <EditModuleLicenseDrawer license={editLicense} onDone={() => setEditLicense(false)} onCreate={refetch} />
      </Page.Content>
    </Page>
  );
};

export default ModulesLicenses;
